const axios = require('axios').default;
import Qs from 'query-string';

import { dateString } from '@ohmsd/utils/formatters';
import bokalSourage from 'services/bokalSourage';
import ROUTES from 'routes';

import api from './client';
import type { ApiResponse, ApiPromise, ApiOpts } from './client';

export function apifyPath (url: string) {
  if (!url.startsWith(ROUTES.API.ROOT))
    url = `${ ROUTES.API.ROOT }${ url }`;
 return url;
}

export function querifyUrl (url: string, query: object): string {
  const qs = Qs.stringify(query);
  const dlmtr = qs ? ~url.indexOf('?') ? '&' : '?' : '';
  return `${ url }${ dlmtr }${ qs }`;
}

export type CacheParams<D> = {
  name,
  request: () => ApiPromise<D>,
  time: number,
  drop: boolean,
};

export function cache<D> ({
  name,
  request,
  time = 6 * 36e5,
  drop = false,
}: CacheParams<D>): ApiPromise<D> {
  let cache = !drop && bokalSourage.get(name);
  if (cache && cache.cached < (Date.now() - (cache.maxAge || time)))
    cache = false;
  if (cache)
    return Promise.resolve(cache);
  return request().then(res => {
    const cacheControl = res.headers['cache-control'];
    const maxAge = cacheControl
      ? cacheControl.split(';').find(d => /max-age/i.test(d.trim()))
      : time;
    bokalSourage.set(name, { data: res.data, cached: Date.now(), maxAge });
    return res;
  });
}

export function handleFetchProgress<D> (
  onProgress: (loadedBytes: number, contentLength: number) => void
)/*: (fetchResp: Response) => ApiPromise<D>*/ {
  return function _handleFetchProgress (fetchResp: Response) {
    const { status, statusText } = fetchResp;
    const res: Partial<Response> = { headers: new Headers(), status, statusText };

    if (status >= 400) {
      return fetchResp.json().then(err => Promise.reject({
        status,
        statusText,
        data: err,
      }));
    }

    const reading = new Promise(resolve => {
      let loadedBytes = 0;
      const reader = fetchResp.clone().body.getReader();
      const contentLength = +fetchResp.headers.get('content-length');
      // eslint-disable-next-line no-console
      console.log(res);
      readStream();

      async function readStream () {
        const { done, value } = await reader.read();
        if (done)
          return setTimeout(() => resolve(loadedBytes), 60);
        loadedBytes += value.length;
        onProgress(loadedBytes, contentLength);
        readStream();
      }
    });

    for (const [key, value] of fetchResp.headers.entries())
      res.headers[key] = value;

    return Promise.all([
      fetchResp.json(),
      reading,
    ]).then(([data]) => Object.assign(res, { data }));
  };
}

export function cancelable<D> (
  opts: ApiOpts & { rejectOnCancel?: boolean }
): ApiResponse<D> {
  const source = axios.CancelToken.source();
  const req = api({ ...opts, cancelToken: source.token })
    .then(res => {
      if (opts.rejectOnCancel && axios.isCancel(res))
        return Promise.reject(res);
      return res;
    });
  req.cancel = () => source.cancel('Canceled');
  return req;
}

export const isCancel =  axios.isCancel;

export function oncePerTime<D> (
  req: <D>(...args: any[]) => ApiPromise<D>,
  time: number = 500
) {
  let p;
  let t = 0;
  return function _oncePerTime (...args) {
    const now = Date.now();
    if (now - time < t)
      return p;
    p = req(...args);
    t = now;
    return p;
  };
}

export function dateRangeQueryFormat ({ from, to, ...query }) {
  return {
    from: dateString(from),
    to: dateString(to),
    ...query,
  };
}